import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ChevronRight, ChevronDown, Video, Bell, CalendarDays,
  Clock, ClipboardCheck, MessageSquare, Plus, Landmark, ClipboardList,
} from 'lucide-react';
import mapi from './api';
import { useMobile } from './MobileContext';
import { MobileShell, C, MobileBottomNav, MobileSheet} from './MobileShell';
import MaMark from './MaMark';
import SchoolSwitchControl from './SchoolSwitchControl';
import ProfileAccountSheet from './ProfileAccountSheet';
import { formatDateWithWeekday, formatClocks, formatTime } from '@/lib/utils';
import { AREA_LABEL, isStaffArea } from './areas';
import {
  dayGreeting, initialsFromName, todayIsoLocal, homeTilesForArea,
} from './homeMenu';
import { tenantLabelForArea, splitTenantBrand } from './tenantLabel';

function go(nav, item) {
  if (item.external) window.open(item.to, '_blank', 'noopener,noreferrer');
  else nav(item.to);
}

function Tile({ item, onClick }) {
  const Icon = item.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={item.id}
      className="relative flex flex-col items-start gap-2 rounded-2xl p-3.5 text-left active:opacity-80 shadow-sm"
      style={{ background: C.card, border: `1px solid ${C.border}`, color: C.text }}
    >
      <span className="w-9 h-9 rounded-xl flex items-center justify-center"
            style={{ background: C.rose }}>
        <Icon className="w-5 h-5" style={{ color: C.red }} />
      </span>
      <span className="min-w-0 w-full">
        <span className="block text-sm font-semibold truncate">{item.label}</span>
        <span className="block text-[11px] truncate mt-0.5" style={{ color: C.dim }}>{item.sub}</span>
      </span>
      {item.badge != null && (
        <span
          className="absolute top-2.5 right-2.5 min-w-[1.15rem] h-5 px-1.5 rounded-full text-[10px] font-bold text-white flex items-center justify-center"
          style={{ background: C.red }}
          data-testid={
            item.id === 'mobile-menu-slot-requests' ? 'mobile-badge-slot-requests'
              : item.id === 'mobile-menu-files' ? 'mobile-badge-files'
                : item.id === 'mobile-menu-plus-hours' ? 'mobile-badge-plus-hours'
                  : undefined
          }
        >
          {item.badge}
        </span>
      )}
    </button>
  );
}

function SecondaryRow({ item, onClick }) {
  const Icon = item.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={item.id}
      className="w-full flex items-center gap-3 px-3.5 py-3 text-left active:opacity-70"
      style={{ background: C.card, borderBottom: `1px solid ${C.border}`, color: C.text }}
    >
      <Icon className="w-5 h-5 shrink-0" style={{ color: C.red }} />
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-medium truncate">{item.label}</span>
        <span className="block text-xs truncate" style={{ color: C.dim }}>{item.sub}</span>
      </span>
      {item.badge != null ? (
        <span
          className="text-xs font-bold px-2 py-0.5 rounded-full text-white"
          style={{ background: C.red }}
          data-testid={
            item.id === 'mobile-menu-slot-requests' ? 'mobile-badge-slot-requests'
              : item.id === 'mobile-menu-files' ? 'mobile-badge-files'
                : item.id === 'mobile-menu-plus-hours' ? 'mobile-badge-plus-hours'
                  : undefined
          }
        >
          {item.badge}
        </span>
      ) : (
        <ChevronRight className="w-4 h-4" style={{ color: C.dim }} />
      )}
    </button>
  );
}

export default function MobileHome() {
  const nav = useNavigate();
  const { me, loading, area, switchArea, endSession, teacherId: activeTeacher, schools } = useMobile();
  const [unread, setUnread] = useState(0);
  const [onlineLessons, setOnlineLessons] = useState([]);
  const [slotRequests, setSlotRequests] = useState(0);
  const [filesBadge, setFilesBadge] = useState(0);
  const [plusBadge, setPlusBadge] = useState(0);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [todayEntries, setTodayEntries] = useState([]);
  const [profileOpen, setProfileOpen] = useState(false);
  const [actionOpen, setActionOpen] = useState(false);
  const [family, setFamily] = useState({ children: [], lessons: [] });

  const staff = isStaffArea(area);
  const teacherId = activeTeacher || me?.teacher_id || null;
  const schoolList = schools || me?.schools || [];

  useEffect(() => {
    if (!me) return;
    mapi.get('/messages/unread-count').then((r) => setUnread(r.data.unread || 0)).catch(() => {});
    mapi.get('/app/notifications').then((r) => {
      setFilesBadge(r.data?.files || 0);
      setPlusBadge(r.data?.plus_hours || 0);
    }).catch(() => {});
    mapi.get('/online-lessons/my-upcoming').then((r) => setOnlineLessons(r.data.lessons || [])).catch(() => {});
    if (staff) {
      mapi.get('/teacher/slot-requests', teacherId ? { params: { teacher_id: teacherId } } : undefined)
        .then((r) => setSlotRequests(r.data?.total || 0))
        .catch(() => setSlotRequests(0));
      if (!teacherId) {
        setTodayEntries([]);
      } else {
        const today = todayIsoLocal();
        mapi.get('/lessons/plan-range', {
          params: {
            start_date: today,
            end_date: today,
            include_students: true,
            teacher_id: teacherId,
          },
        })
          .then((r) => {
            const list = (r.data?.entries || [])
              .filter((e) => !e.cancelled)
              .filter((e) => String(e.teacher_id || '') === String(teacherId))
              .sort((a, b) => String(a.start).localeCompare(String(b.start)));
            setTodayEntries(list);
          })
          .catch(() => setTodayEntries([]));
      }
    } else {
      setSlotRequests(0);
      setTodayEntries([]);
    }
    if (area === 'eltern') {
      mapi.get('/app/children')
        .then((r) => setFamily({
          children: r.data?.children || [],
          lessons: r.data?.lessons || [],
        }))
        .catch(() => setFamily({ children: [], lessons: [] }));
    } else {
      setFamily({ children: [], lessons: [] });
    }
  }, [me, area, staff, teacherId]);

  const greet = useMemo(() => dayGreeting(me?.full_name), [me?.full_name]);
  const initials = useMemo(() => initialsFromName(me?.full_name), [me?.full_name]);
  const dateLabel = useMemo(
    () => new Date().toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }),
    [],
  );
  const tiles = useMemo(
    () => homeTilesForArea(area, { unread, slotRequests, files: filesBadge, plusHours: plusBadge }),
    [area, unread, slotRequests, filesBadge, plusBadge],
  );

  if (loading) {
    return (
      <MobileShell bottomPad={false}>
        <div className="flex-1 flex items-center justify-center" style={{ color: C.dim }}>Laden…</div>
      </MobileShell>
    );
  }
  if (!me) return <Navigate to="/m/login" replace />;

  const selectedSchool = schoolList.find((school) => school.teacher_id === teacherId);
  const tenantLabel = (staff && selectedSchool?.tenant_name)
    ? selectedSchool.tenant_name
    : tenantLabelForArea(me, area);
  const { brand: tenantBrand, place: tenantPlace } = splitTenantBrand(tenantLabel);

  const pickArea = (a) => {
    switchArea(a);
    setProfileOpen(false);
  };

  const leaveForAuth = async (query) => {
    setProfileOpen(false);
    await endSession();
    // Hard navigation — sonst greift <Navigate to="/m/login"> ohne Query-Parameter.
    window.location.href = `/m/login${query || ''}`;
  };

  const openOffice = async () => {
    try {
      const [people, chats] = await Promise.all([
        mapi.get('/app/contacts'),
        mapi.get('/messages/conversations'),
      ]);
      const verwaltung = (people.data || []).filter((row) => row.person_type === 'admin' && row.user_id);
      const ids = new Set(verwaltung.map((row) => String(row.user_id)));
      if (!ids.size) {
        toast.error('Keine Verwaltung erreichbar');
        return;
      }
      const vorhandener = (Array.isArray(chats.data) ? chats.data : []).find((chat) => {
        if (chat.kind && chat.kind !== 'direct') return false;
        const andere = (chat.participants || [])
          .map((person) => String(person.id))
          .filter((id) => id !== String(me?.id));
        return andere.length === 1 && ids.has(andere[0]);
      });
      if (vorhandener) {
        nav(`/m/chat/${vorhandener.id}`);
        return;
      }
      const created = await mapi.post('/messages/conversations', {
        kind: 'direct',
        participant_ids: [verwaltung[0].user_id],
      });
      nav(`/m/chat/${created.data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Der Chat mit der Verwaltung konnte nicht geöffnet werden');
    }
  };

  const actionItems = staff
    ? [
      { id: 'act-attendance', icon: ClipboardCheck, label: 'Anwesenheit', to: '/m/attendance' },
      { id: 'act-plus', icon: Clock, label: 'Plusstunde melden', to: '/m/plus-hours' },
      { id: 'act-msg', icon: MessageSquare, label: 'Nachricht schreiben', to: '/m/messages' },
      { id: 'act-slots', icon: CalendarDays, label: 'Terminanfragen', to: '/m/terminanfragen' },
    ]
    : [
      { id: 'act-msg', icon: MessageSquare, label: 'Nachricht schreiben', to: '/m/messages' },
      ...(area === 'eltern' ? [
        { id: 'act-office', icon: Landmark, label: 'Nachricht an Verwaltung', office: true },
        { id: 'act-enroll', icon: ClipboardList, label: 'Anmeldung', enroll: true },
      ] : []),
      { id: 'act-docs', icon: Plus, label: 'Dokumente', to: '/m/documents' },
    ];

  return (
    <MobileShell
      bottomNav={(
        <MobileBottomNav
          unread={unread}
          onAction={() => setActionOpen(true)}
        />
      )}
    >
      {/* Kopf: Balken bis oben, Schrift unter Uhr und Akku */}
      <div className="mobile-below-clock sticky top-0 z-20 px-4 pb-3 flex items-start justify-between gap-3"
           data-testid="mobile-home-header"
           style={{ background: C.header, color: '#fff' }}>
        <div className="flex items-start gap-2.5 min-w-0 flex-1">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 overflow-hidden"
               style={{ background: '#FFFFFF' }}
               data-testid="mobile-home-logo-frame">
            <MaMark className="w-10 h-10" testId="mobile-home-logo" alt="Musik Akademie" />
          </div>
          <div className="min-w-0 pt-0.5" data-testid="mobile-home-tenant">
            <p className="text-sm font-bold leading-tight" style={{ color: '#fff' }}
               data-testid="mobile-home-tenant-brand">
              {tenantBrand}
            </p>
            {schoolList.length > 1 && (area === 'lehrer' || area === 'personal') ? (
              <SchoolSwitchControl light />
            ) : tenantPlace && (
              <p className="text-[12px] font-medium leading-tight mt-0.5 truncate"
                 style={{ color: 'rgba(255,255,255,0.82)' }}
                 data-testid="mobile-home-tenant-place">
                {tenantPlace}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-start shrink-0">
          <button
            type="button"
            onClick={() => setProfileOpen(true)}
            data-testid="mobile-area-switch"
            className="flex items-center gap-2 rounded-full pl-1 pr-2.5 py-1"
            style={{ background: 'rgba(255,255,255,0.14)' }}
          >
            <span className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold"
                  style={{ background: '#fff', color: C.red }} data-testid="mobile-home-avatar">
              {initials}
            </span>
            <span className="text-left min-w-0 max-w-[7rem]">
              <span className="block text-[11px] font-semibold truncate leading-tight" style={{ color: '#fff' }}>{me.full_name}</span>
              <span className="flex items-center gap-0.5 text-[10px] leading-tight mt-0.5" style={{ color: 'rgba(255,255,255,0.82)' }}>
                {AREA_LABEL[area] || area}
                <ChevronDown className="w-3 h-3" />
              </span>
            </span>
          </button>
        </div>
      </div>

      <div className="px-4 pt-4 pb-2">
        <h1 className="text-2xl font-bold tracking-tight" data-testid="mobile-home-greeting">{greet.line}</h1>
        <p className="flex items-center gap-1.5 text-xs mt-1" style={{ color: C.dim }} data-testid="mobile-home-date">
          <CalendarDays className="w-3.5 h-3.5" />
          <span className="capitalize">{dateLabel}</span>
        </p>
      </div>

      {!me.identities.length && (
        <button
          type="button"
          onClick={() => nav('/m/settings/identities')}
          data-testid="mobile-no-identity-banner"
          className="mx-4 mb-3 text-left text-sm rounded-xl px-4 py-3"
          style={{ background: C.rose, border: `1px solid ${C.border}`, color: C.text }}
        >
          Noch keine Kennung freigeschaltet — tippe hier, um Kennung + PIN einzugeben.
        </button>
      )}

      {area === 'eltern' && (
        <>
          <section className="mx-4 mb-4 rounded-2xl overflow-hidden shadow-sm"
                   style={{ background: C.card, border: `1px solid ${C.border}` }}
                   data-testid="mobile-my-children">
            <h2 className="text-base font-bold px-3.5 pt-3 pb-2">Meine Kinder</h2>
            {family.children.length === 0 ? (
              <p className="px-3.5 pb-3.5 text-xs" style={{ color: C.dim }} data-testid="mobile-children-empty">
                Diesem Zugang sind keine Kinder zugeordnet.
              </p>
            ) : (
              <ul className="px-3.5 pb-2">
                {family.children.map((child, i) => (
                  <li
                    key={child.id}
                    className="py-2 text-sm font-medium"
                    style={{ borderTop: i ? `1px solid ${C.border}` : undefined }}
                    data-testid={`mobile-child-${child.id}`}
                  >
                    {child.name}
                  </li>
                ))}
              </ul>
            )}
          </section>
          <section className="mx-4 mb-4 rounded-2xl overflow-hidden shadow-sm"
                   style={{ background: C.card, border: `1px solid ${C.border}` }}
                   data-testid="mobile-child-lessons">
            <h2 className="text-base font-bold px-3.5 pt-3 pb-2">Unterricht der Kinder</h2>
            {family.lessons.length === 0 ? (
              <p className="px-3.5 pb-3.5 text-xs" style={{ color: C.dim }}>
                Kein kommender Unterricht.
              </p>
            ) : (
              <ul className="px-3.5 pb-2">
                {family.lessons.map((lesson, i) => (
                  <li
                    key={`${lesson.id}-${lesson.child_name}`}
                    className="py-2.5"
                    style={{ borderTop: i ? `1px solid ${C.border}` : undefined }}
                    data-testid={`mobile-child-lesson-${lesson.id}`}
                  >
                    <span className="block text-sm font-semibold">{lesson.child_name}</span>
                    <span className="block text-xs mt-0.5" style={{ color: C.dim }}>
                      {lesson.date} · {lesson.start}–{lesson.end} · {lesson.course_name}
                      {lesson.teacher_name ? ` · ${lesson.teacher_name}` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}

      {/* Lehrer: heutiger Unterricht */}
      {staff && (
        <section className="mx-4 mb-4 rounded-2xl overflow-hidden shadow-sm"
                 style={{ background: C.card, border: `1px solid ${C.border}` }}
                 data-testid="mobile-today-lessons">
          <div className="flex items-center justify-between px-3.5 pt-3 pb-2">
            <h2 className="text-base font-bold">Mein Unterricht heute</h2>
            <button
              type="button"
              onClick={() => nav('/m/unterricht')}
              className="text-[11px] font-semibold"
              style={{ color: C.red }}
              data-testid="mobile-today-all"
            >
              Alle Termine ›
            </button>
          </div>
          {todayEntries.length === 0 ? (
            <p className="px-3.5 pb-3.5 text-xs" style={{ color: C.dim }} data-testid="mobile-today-empty">
              Heute kein geplanter Unterricht.
            </p>
          ) : (
            <ul className="px-3.5 pb-2 space-y-0">
              {todayEntries.slice(0, 5).map((e, i) => {
                const who = e.student_label
                  || (Array.isArray(e.students) && e.students[0]?.name)
                  || e.teacher_name
                  || '';
                const room = [e.room_name, e.location_name].filter(Boolean).join(' · ')
                  || (e.is_online ? 'Online' : '');
                return (
                  <li
                    key={`${e.course_id}-${e.start}-${i}`}
                    className="flex items-start gap-2.5 py-2.5"
                    style={{ borderTop: i ? `1px solid ${C.border}` : undefined }}
                    data-testid={`mobile-today-row-${e.course_id}`}
                  >
                    <span className="w-11 shrink-0 text-xs font-bold tabular-nums pt-0.5">{formatTime(e.start)}</span>
                    <span className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ background: C.red }} />
                    <span className="flex-1 min-w-0">
                      <span className="block text-xs font-semibold truncate">{formatClocks(e.name) || e.name || 'Unterricht'}</span>
                      {who && <span className="block text-[11px] truncate" style={{ color: C.dim }}>{who}</span>}
                    </span>
                    {room && (
                      <span className="text-[10px] font-medium shrink-0 max-w-[5.5rem] text-right leading-tight"
                            style={{ color: C.dim }}>{room}</span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          <button
            type="button"
            onClick={() => nav('/m/unterricht')}
            className="w-full flex items-center justify-center gap-1.5 py-2.5 text-xs font-semibold"
            style={{ borderTop: `1px solid ${C.border}`, color: C.red }}
            data-testid="mobile-today-open-plan"
          >
            <CalendarDays className="w-3.5 h-3.5" />
            Tagesplan öffnen
          </button>
        </section>
      )}

      {onlineLessons.length > 0 && (
        <div className="mx-4 mb-3 rounded-xl px-4 py-3" data-testid="mobile-online-lessons-banner"
             style={{ background: C.successBg, border: `1px solid ${C.border}` }}>
          <div className="flex items-center gap-2 mb-2">
            <Video className="w-4 h-4" style={{ color: C.success }} />
            <span className="text-sm font-semibold" style={{ color: '#065F46' }}>Onlineunterricht</span>
          </div>
          {onlineLessons.slice(0, 3).map((l) => (
            <button key={`${l.course_id}-${l.date}`} type="button" onClick={() => nav(`/m/online/${l.course_id}`)}
                    data-testid={`mobile-online-lesson-${l.course_id}-${l.date}`}
                    className="w-full flex items-center gap-2 py-1.5 text-left active:opacity-70">
              <span className="flex-1 min-w-0">
                <span className="block text-xs font-medium truncate" style={{ color: C.text }}>{formatClocks(l.course_name) || l.course_name}</span>
                <span className="block text-[10px]" style={{ color: C.dim }}>
                  {formatDateWithWeekday(l.date + 'T00:00:00', { year: false })}
                  {l.time ? ` · ${formatTime(l.time)} Uhr` : ''}{l.teacher_name ? ` · ${l.teacher_name}` : ''}
                </span>
              </span>
              <ChevronRight className="w-4 h-4 shrink-0" style={{ color: C.success }} />
            </button>
          ))}
        </div>
      )}

      {/* Primär-Kacheln */}
      <div className="px-4 mb-4 grid grid-cols-2 gap-2.5" data-testid="mobile-home-tiles">
        {tiles.primary.map((it) => (
          <Tile key={it.id} item={it} onClick={() => go(nav, it)} />
        ))}
      </div>

      {unread > 0 && (
        <button
          type="button"
          onClick={() => nav('/m/messages')}
          className="mx-4 mb-4 flex items-center gap-3 rounded-xl px-3.5 py-3 text-left shadow-sm active:opacity-80"
          style={{ background: C.card, border: `1px solid ${C.border}` }}
          data-testid="mobile-home-notify-banner"
        >
          <Bell className="w-4 h-4 shrink-0" style={{ color: C.red }} />
          <span className="text-xs font-medium flex-1">
            {unread} neue Nachricht{unread === 1 ? '' : 'en'}
          </span>
          <ChevronRight className="w-4 h-4" style={{ color: C.dim }} />
        </button>
      )}

      {/* Sekundär-Liste */}
      <div className="mx-4 mb-4 rounded-2xl overflow-hidden shadow-sm"
           style={{ border: `1px solid ${C.border}` }}
           data-testid="mobile-home-secondary">
        {tiles.secondary.map((it) => (
          <SecondaryRow key={it.id} item={it} onClick={() => go(nav, it)} />
        ))}
      </div>

      <div className="px-4 pb-4 flex justify-center gap-4 text-[10px]" style={{ color: C.dim }}>
        <span>Impressum</span>·<span>Datenschutz</span>·<span>Verhaltensregeln</span>
      </div>

      {profileOpen && (
        <ProfileAccountSheet
          me={me}
          area={area}
          onClose={() => setProfileOpen(false)}
          onPickArea={pickArea}
          onSwitchAccount={(account) => {
            const email = encodeURIComponent(account.email || '');
            leaveForAuth(email ? `?email=${email}` : '');
          }}
          onNewRegistration={() => leaveForAuth('?mode=register')}
          onLoginOther={() => leaveForAuth('')}
        />
      )}

      {/* Aktion (+) */}
      {actionOpen && (
        <MobileSheet className="fixed inset-0 z-50 flex items-end justify-center" style={{ background: 'rgba(0,0,0,0.45)' }}
             onClick={() => setActionOpen(false)} data-testid="mobile-action-sheet">
          <div className="w-full max-w-md rounded-t-2xl p-4 space-y-1" style={{ background: C.card }}
               onClick={(e) => e.stopPropagation()}>
            <p className="text-sm font-bold px-1 mb-2">Aktion wählen</p>
            {actionItems.map((it) => {
              const Icon = it.icon;
              return (
                <button key={it.id} type="button"
                        data-testid={it.id}
                        onClick={() => {
                          setActionOpen(false);
                          if (it.office) { openOffice(); return; }
                          if (it.enroll) { setEnrollOpen(true); return; }
                          nav(it.to);
                        }}
                        className="w-full flex items-center gap-3 rounded-xl px-3 py-3 text-left active:opacity-80"
                        style={{ background: C.bg, border: `1px solid ${C.border}` }}>
                  <Icon className="w-5 h-5" style={{ color: C.red }} />
                  <span className="text-sm font-medium flex-1">{it.label}</span>
                  <ChevronRight className="w-4 h-4" style={{ color: C.dim }} />
                </button>
              );
            })}
            <button type="button" onClick={() => setActionOpen(false)}
                    className="w-full py-3 mt-2 text-sm font-semibold rounded-xl"
                    style={{ color: C.dim }}>
              Schließen
            </button>
          </div>
        </MobileSheet>
      )}

      {enrollOpen && (
        <MobileSheet className="fixed inset-0 z-50 flex items-end justify-center" style={{ background: 'rgba(0,0,0,0.45)' }}
                     onClick={() => setEnrollOpen(false)} data-testid="mobile-enroll-sheet">
          <div className="w-full max-w-md rounded-t-2xl p-5 space-y-2" style={{ background: C.card }}
               onClick={(event) => event.stopPropagation()}>
            <p className="text-sm font-bold">Anmeldung</p>
            <p className="text-xs" style={{ color: C.dim }}>
              Hier können Zahlungspflichtige später ein weiteres Kind, ein zusätzliches Fach
              oder eine Karte anmelden. Die Verbindung zum Online-Formular folgt.
            </p>
            <button type="button" onClick={() => setEnrollOpen(false)}
                    className="w-full py-3 text-sm font-semibold rounded-xl" style={{ color: C.dim }}>
              Schließen
            </button>
          </div>
        </MobileSheet>
      )}
    </MobileShell>
  );
}
